export enum ClientStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending'
}

export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * Project entity (UI: Projects). Stored in `projects/` (and legacy `clients/` docs).
 * Contact fields are deprecated — use the linked client account (`clientId`).
 */
export interface Client {
  id: string;
  entityType?: 'project' | 'project_legacy' | 'client';
  name: string;
  /** @deprecated Prefer linked client account email */
  email?: string;
  /** @deprecated Prefer linked client account phone */
  phone?: string;
  /** @deprecated Prefer linked client account address */
  address?: Address;
  status: ClientStatus;
  clientId?: string;
  organizationId?: string; // legacy
  logoUrl?: string;
  quickbooksCompanyId?: string;
  jiraProjectId?: string;
  githubRepoId?: number;
  githubRepoOwner?: string;
  githubRepoName?: string;
  githubInstallationId?: number | string;
  githubDefaultBranch?: string;
  githubSync?: {
    issuesIn?: boolean;
    branchesIn?: boolean;
    taskWriteBack?: boolean;
  };
  /** Controls which integration sections appear in the task modal for this project. */
  taskFieldVisibility?: {
    github?: boolean;
    jira?: boolean;
  };
  /** Project-specific task versions available in task dialogs. */
  taskVersions?: string[];
  /**
   * Product surfaces of this project (e.g. driver app, builder app, ops admin).
   * `id` matches the GitHub label for that surface so issue sync can set it.
   * Clients are asked to pick one when they file a request from ChatGPT.
   */
  platforms?: ProjectPlatform[];
  /** Project-specific task labels available in task dialogs. */
  taskLabels?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ClientWithOrg extends Client {
  organizationName?: string;
}

export interface ContactPerson {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  isPrimary: boolean;
}

export interface ProjectPlatform {
  id: string;
  name: string;
}

/** "Driver app" → "driver-app": the id, and the GitHub label it matches. */
export function platformIdFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function platformName(
  platforms: ProjectPlatform[] | undefined,
  id: string | null | undefined
): string | null {
  if (!id) return null;
  return platforms?.find((p) => p.id === id)?.name ?? id;
}
